import { Box, Stack, SxProps } from '@mui/material';
import { PropsWithChildren, ReactNode, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { Breadcrumb } from '../../breadcrumbs/models/Breadcrumb';
import { NAVBAR_LIGHT_THEME } from '../../config/theme/navBarTheme';
import { Breadcrumbs } from './Breadcrumbs';
import { NavTabs } from './NavTabs';
import { PageTitle } from './PageTitle';
import { Route } from '../../routing/routes';

type SecondaryNavProps = PropsWithChildren<{
  title: string;
  subtitle?: ReactNode;
  rightSection?: ReactNode;
  tabs?: Route[];
  getBreadcrumbs: () => Breadcrumb[];
  loading?: boolean;
}>;

const STYLES: Record<string, SxProps> = {
  TITLE: { px: 'var(--secondary-outlet-px)', pt: 2, zIndex: 2, width: 1, backgroundColor: 'screenBackground.main' },
  TABS_CONTAINER: { backgroundColor: 'screenBackground.main', borderBottom: '1px solid lightgray' },
  TABS: { px: 'var(--secondary-outlet-px)' },
};

/**
 * `SecondaryNav` is the parent of many controllers, at least all those under a tabbed layout
 * such as `ProjectController` and `ReportController`. It is important to
 * prevent as many re-renders as possible, because they would cascade and re-render everything it wraps.
 * While it is true that the props contain several non-primitive values and it's not trivial to ensure
 * that their object references won't change, wrapping it with `memo` will give an advantage to those
 * controllers who implement thorough checks on how these props are passed down.
 */
export const SecondaryNav = memo((props: SecondaryNavProps) => {
  const { title, subtitle, rightSection, tabs, getBreadcrumbs, loading = false, children } = props;
  const { pathname } = useLocation();

  const showNavTabs = tabs && tabs.length > 0 && !loading;

  return (
    <Stack id="cc-secondary-nav">
      <Box sx={STYLES.TITLE}>
        <Breadcrumbs key={pathname} getBreadcrumbs={getBreadcrumbs} loading={loading} />
        <PageTitle title={title} subtitle={subtitle} rightSection={rightSection} margin={tabs && tabs.length > 0} loading={loading} />
      </Box>
      {showNavTabs && (
        <Box id="cc-secondary-navtabs" sx={STYLES.TABS_CONTAINER}>
          <NavTabs sx={STYLES.TABS} tabs={tabs} theme={NAVBAR_LIGHT_THEME} />
        </Box>
      )}
      <Box sx={{ flex: 1, px: 'var(--secondary-outlet-px)', py: 'var(--secondary-outlet-py)' }}>{children}</Box>
    </Stack>
  );
});
